import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IconImageBroken } from '../icons';

const FallbackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 80px;
  background: ${({ theme }) => theme.colors.highlight.gray25};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  gap: 8px;
`;

const StyledImg = styled.img<{ $objectFit: 'contain' | 'cover' }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) => $objectFit};
  display: block;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.highlight.gray25};
`;

export interface ImageLoadFallbackProps {
  /** Подпись под иконкой (например, «Не найден», «Нет обложки»). */
  label?: string;
  /** Размер иконки в пикселях. */
  iconSize?: number;
}

/** Заглушка при ошибке загрузки изображения или отсутствии URL: иконка + подпись. */
export function ImageLoadFallback({
  label = 'Не найден',
  iconSize = 48,
}: ImageLoadFallbackProps) {
  return (
    <FallbackWrapper role="img" aria-label={label}>
      <IconImageBroken size={iconSize} />
      <span>{label}</span>
    </FallbackWrapper>
  );
}

export type ObjectFit = 'contain' | 'cover';

export interface ImageWithFallbackProps {
  /** URL изображения. Если пустой — показывается заглушка с fallbackLabel или «Нет изображения». */
  src?: string | null;
  /** Alt-текст для доступности. */
  alt: string;
  /** object-fit для img: contain (превью), cover (обложки). */
  objectFit?: ObjectFit;
  /** Подпись на заглушке при ошибке загрузки или при отсутствии src. */
  fallbackLabel?: string;
  /** Класс для контейнера. */
  className?: string;
  /** Стили для контейнера (например, width/height). */
  style?: React.CSSProperties;
}

/**
 * Изображение по URL с заглушкой при ошибке загрузки (404, сеть) или при отсутствии src.
 * Не показывает «битую» картинку — при onError рендерит ImageLoadFallback.
 */
export function ImageWithFallback({
  src,
  alt,
  objectFit = 'cover',
  fallbackLabel,
  className,
  style,
}: ImageWithFallbackProps) {
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
  }, [src]);

  const showFallback = !src || error;
  const label = fallbackLabel ?? (src ? 'Не найден' : 'Нет изображения');

  if (showFallback) {
    return (
      <Wrapper className={className} style={style}>
        <ImageLoadFallback label={label} />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className} style={style}>
      <StyledImg
        src={src}
        alt={alt}
        $objectFit={objectFit}
        onError={() => setError(true)}
      />
    </Wrapper>
  );
}
